import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { getMyReminders, updateCommTaskStatus, completeCommFollowUp } from "../lib/api";

function fmtDate(d) {
  if (!d) return "";
  try {
    return new Date(d + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" });
  } catch {
    return d;
  }
}

function ItemRow({ item, onDone }) {
  const isTask = item.kind === "task";
  const isFollowUp = item.kind === "follow_up";
  const headline = isFollowUp
    ? (item.subject || item.contact_name || "Follow-up")
    : item.description;
  const context = isFollowUp
    ? (item.description && item.description !== "Follow-up" ? item.description : null)
    : (item.subject || item.contact_name || null);

  return (
    <div className="flex items-start gap-3 py-2 border-b last:border-b-0" data-testid={`my-reminder-${item.kind}-${item.task_id || item.comm_id}`}>
      <input
        type="checkbox"
        className="mt-1"
        checked={false}
        onChange={() => onDone(item)}
        title="Mark done"
        data-testid={`my-reminder-done-${item.task_id || item.comm_id}`}
      />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-gray-900 truncate">{headline}</div>
        <div className="text-xs text-muted-foreground flex items-center gap-2 flex-wrap mt-0.5">
          {isTask && <Badge variant="outline" className="bg-sky-50 text-sky-700 text-[10px] py-0">Task</Badge>}
          {isFollowUp && <Badge variant="outline" className="bg-amber-50 text-amber-700 text-[10px] py-0">Follow-up</Badge>}
          {item.student_name && <span>· {item.student_name}</span>}
          {context && <span className="truncate">· {context}</span>}
        </div>
      </div>
      {item.due_date && <span className="text-xs font-medium text-gray-600 whitespace-nowrap">{fmtDate(item.due_date)}</span>}
    </div>
  );
}

export default function MyTasksWidget() {
  const navigate = useNavigate();
  const [data, setData] = useState({ overdue: [], today: [], upcoming: [], total: 0 });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const res = await getMyReminders();
      setData(res.data);
    } catch {
      // silent — widget is non-critical
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, []);

  const handleDone = async (item) => {
    try {
      if (item.kind === "task") {
        await updateCommTaskStatus(item.comm_id, item.task_id, "done");
      } else {
        await completeCommFollowUp(item.comm_id);
      }
      toast.success("Marked done");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update");
    }
  };

  if (loading) return null;
  if (data.total === 0) {
    return (
      <Card data-testid="my-tasks-widget-empty">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base">My Tasks & Reminders</CardTitle>
              <CardDescription>You're all caught up. 🎉</CardDescription>
            </div>
            <Button size="sm" variant="outline" onClick={() => navigate("/communications")} data-testid="my-tasks-open-page">
              Open
            </Button>
          </div>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card className="animate-fade-in" data-testid="my-tasks-widget">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              My Tasks & Reminders
              <Badge className="bg-[#00305E] text-white">{data.total}</Badge>
            </CardTitle>
            <CardDescription>From your communications log — click the checkbox to mark done.</CardDescription>
          </div>
          <Button size="sm" variant="outline" onClick={() => navigate("/communications")} data-testid="my-tasks-open-page">
            See all
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4 pt-0">
        {data.overdue.length > 0 && (
          <div data-testid="my-tasks-overdue-section">
            <div className="text-xs font-semibold uppercase tracking-wide text-red-700 mb-1 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-red-600 inline-block" />
              Overdue ({data.overdue.length})
            </div>
            <div className="bg-red-50 rounded-md px-3 border border-red-200">
              {data.overdue.map((it) => <ItemRow key={`o-${it.task_id || it.comm_id}`} item={it} onDone={handleDone} />)}
            </div>
          </div>
        )}
        {data.today.length > 0 && (
          <div data-testid="my-tasks-today-section">
            <div className="text-xs font-semibold uppercase tracking-wide text-orange-700 mb-1 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-orange-500 inline-block" />
              Due Today ({data.today.length})
            </div>
            <div className="bg-orange-50 rounded-md px-3 border border-orange-200">
              {data.today.map((it) => <ItemRow key={`t-${it.task_id || it.comm_id}`} item={it} onDone={handleDone} />)}
            </div>
          </div>
        )}
        {data.upcoming.length > 0 && (
          <div data-testid="my-tasks-upcoming-section">
            <div className="text-xs font-semibold uppercase tracking-wide text-blue-700 mb-1 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-blue-500 inline-block" />
              Upcoming (next 14 days, {data.upcoming.length})
            </div>
            <div className="bg-blue-50/40 rounded-md px-3 border border-blue-100">
              {data.upcoming.map((it) => <ItemRow key={`u-${it.task_id || it.comm_id}`} item={it} onDone={handleDone} />)}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
